import { useState, useCallback } from 'react';
import { FeedContext } from './FeedContext';
import {
  fetchPostsService,
  fetchSavedPostsService,
  fetchSubscribedPostsService,
  fetchArtisanPostsService,
  newPostService,
  savePostService,
  unsavePostService,
  fetchArtisanFollowersService,
  deleteArtisanPostService,
} from '../../features/feed/services/feed.service';
import { fetchFollowedArtisansService } from '../../global-services/user.service';
import { toast } from 'sonner';

export const FeedProvider = ({ children }) => {
  const [postsLoading, setPostsLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
  const [error, setError] = useState(null);
  const [postUploading, setPostUploading] = useState(false);

  const fetchPosts = useCallback(async (activeTab, searchParams) => {
    try {
      setPostsLoading(true);
      const data = await fetchPostsService(activeTab, searchParams);
      setPosts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setPostsLoading(false);
    }
  }, []);

  const savePost = useCallback(async (postId) => {
    try {
      await savePostService(postId);
      const updatedSaved = await fetchSavedPostsService();
      setSavedPosts(updatedSaved);
      toast.success('Post Saved Successfully');
    } catch (err) {
      toast.error('Failed to Save Post');
    }
  }, []);

  const unsavePost = useCallback(async (postId, activeTab) => {
    try {
      await unsavePostService(postId);
      const updatedSaved = await fetchSavedPostsService();
      setSavedPosts(updatedSaved);
      if (activeTab) {
        const updatedPosts = await fetchPostsService(activeTab);
        setPosts(updatedPosts);
      }
      toast.success('Post UnSaved Successfully');
    } catch (err) {
      toast.error('Failed to UnSave Post');
    }
  }, []);

  const newPost = useCallback(async (formData) => {
    setPostUploading(true);
    try {
      const data = await newPostService(formData);
      toast.success('Post Created Successfully');
      setPosts((prev) => [data.post, ...prev]);
      return data.post;
    } catch (err) {
      toast.error('Failed to Create Post');
      throw err;
    } finally {
      setPostUploading(false);
    }
  }, []);

  const fetchSavedPosts = useCallback(async () => {
    try {
      const data = await fetchSavedPostsService();
      setSavedPosts(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setPostsLoading(false);
    }
  }, []);

  const fetchSubscribedPosts = useCallback(async (activeTab, followedFilters) => {
    try {
      const data = await fetchSubscribedPostsService(activeTab, followedFilters);
      return data;
    } catch (err) {
      setError(err.message);
    } finally {
      setPostsLoading(false);
    }
  }, []);

  const fetchArtisanPosts = useCallback(async (artisanId) => {
    try {
      const data = await fetchArtisanPostsService(artisanId);
      return data;
    } catch (err) {
      setError(err.message);
    } finally {
      setPostsLoading(false);
    }
  }, []);

  const fetchFollowedArtisans = useCallback(async () => {
    try {
      const data = await fetchFollowedArtisansService();
      return data;
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const fetchArtisanFollowers = useCallback(async () => {
    try {
      const data = await fetchArtisanFollowersService();
      return data;
    } catch (err) {
      setError(err.message);
    }
  }, []);

  const deleteArtisanPost = useCallback(async (postId) => {
    try {
      await deleteArtisanPostService(postId);
      toast.success('Post Deleted Successfully');
    } catch (err) {
      toast.error('Failed to Delete Post');
      throw err;
    }
  }, []);

  return (
    <FeedContext.Provider
      value={{
        posts,
        postsLoading,
        error,
        postUploading,
        setPostUploading,
        fetchPosts,
        savePost,
        unsavePost,
        newPost,
        fetchSavedPosts,
        savedPosts,
        setSavedPosts,
        setPostsLoading,
        fetchSubscribedPosts,
        fetchArtisanPosts,
        fetchFollowedArtisans,
        fetchArtisanFollowers,
        deleteArtisanPost,
      }}
    >
      {children}
    </FeedContext.Provider>
  );
};
